import { useRef, useCallback, type TouchEvent } from 'react';

interface UseSwipeOptions {
  /** Called with the horizontal pixel distance (negative = left, positive = right) */
  onSwipe?: (deltaX: number) => void;
  /** Minimum horizontal distance to count as a swipe */
  threshold?: number;
}

export function useSwipe({ onSwipe, threshold = 30 }: UseSwipeOptions) {
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const onTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  }, []);

  const onTouchEnd = useCallback((e: TouchEvent) => {
    const touch = e.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    // Only trigger if horizontal swipe is dominant (not vertical scrolling)
    if (Math.abs(deltaX) < threshold || Math.abs(deltaY) > Math.abs(deltaX)) {
      return;
    }

    onSwipe?.(deltaX);
  }, [onSwipe, threshold]);

  return { onTouchStart, onTouchEnd };
}
